import React, { useState } from 'react';
import { contactData } from '../data/mock';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    tourDate: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    toast.success('Thank you for your inquiry. We will contact you shortly.');
    setFormData({ name: '', email: '', phone: '', message: '', tourDate: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="font-lato text-[#D4AF37] uppercase tracking-[0.3em] text-sm mb-4 block">Get in Touch</span>
          <h2 className="font-playfair text-4xl md:text-5xl text-[#1A2B3C] mb-4">Begin Your Journey</h2>
          <p className="font-lato text-[#1A2B3C]/60 max-w-2xl mx-auto">
            Schedule a private tour of our facilities and discover the FIJA difference firsthand.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2">
            <div className="bg-[#1A2B3C] rounded-xl p-8 h-full">
              <h3 className="font-playfair text-2xl text-white mb-8">Contact Information</h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-lato text-white font-medium mb-1">Main Office</h4>
                    <p className="font-lato text-white/60 text-sm">{contactData.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-lato text-white font-medium mb-1">Phone</h4>
                    <p className="font-lato text-white/60 text-sm">{contactData.phone}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-lato text-white font-medium mb-1">Email</h4>
                    <p className="font-lato text-white/60 text-sm">{contactData.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-lato text-white font-medium mb-1">Hours</h4>
                    <p className="font-lato text-white/60 text-sm">Reception: {contactData.receptionHours}</p>
                    <p className="font-lato text-white/60 text-sm">Care: {contactData.careHours}</p>
                  </div>
                </div>
              </div>

              {/* Decorative element */}
              <div className="mt-10 pt-8 border-t border-white/10">
                <p className="font-playfair text-[#D4AF37] text-lg italic">
                  "Excellence is the only standard we accept."
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="font-playfair text-2xl text-[#1A2B3C] mb-6">Book a Private Tour</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="font-lato text-sm text-[#1A2B3C]/70 mb-2 block">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#1A2B3C]/20 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 font-lato transition-all duration-300"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="font-lato text-sm text-[#1A2B3C]/70 mb-2 block">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#1A2B3C]/20 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 font-lato transition-all duration-300"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="font-lato text-sm text-[#1A2B3C]/70 mb-2 block">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-[#1A2B3C]/20 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 font-lato transition-all duration-300"
                    placeholder="+44 000 000 0000"
                  />
                </div>
                <div>
                  <label className="font-lato text-sm text-[#1A2B3C]/70 mb-2 block">Preferred Tour Date</label>
                  <input
                    type="date"
                    name="tourDate"
                    value={formData.tourDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-[#1A2B3C]/20 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 font-lato transition-all duration-300"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="font-lato text-sm text-[#1A2B3C]/70 mb-2 block">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-[#1A2B3C]/20 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/20 font-lato resize-none transition-all duration-300"
                  placeholder="Tell us about your requirements..."
                />
              </div>

              <button
                type="submit"
                className="book-tour-btn w-full flex items-center justify-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#1A2B3C] rounded-lg hover:bg-[#D4AF37]/90 transition-all duration-300 font-lato font-semibold"
              >
                <Send className="w-5 h-5" />
                Request Tour
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
